import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, api } from "@/lib/api";
import { Sparkles, Send, User, Trash2 } from "lucide-react";

const SUGGESTIONS = [
  "How am I related to everyone?",
  "Who is the oldest person in the tree?",
  "Tell me about my grandparents.",
  "Where did my family come from?",
];

export function AssistantDialog({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const [clearing, setClearing] = useState(false);
  const sessionRef = useRef(null);
  const idRef = useRef(0);
  const scrollRef = useRef(null);
  const nextId = () => ++idRef.current;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // On first open, restore the most recent conversation so it's remembered across visits.
  useEffect(() => {
    if (!open || loadedHistory) return;
    setLoadedHistory(true);
    api.getAssistantHistory().then((d) => {
      if (d?.session_id && Array.isArray(d.messages) && d.messages.length) {
        sessionRef.current = d.session_id;
        setMessages(d.messages.map((m) => ({ id: nextId(), role: m.role, content: m.content })));
      }
    }).catch(() => {});
  }, [open, loadedHistory]);

  const clearChat = useCallback(async () => {
    if (busy || clearing) return;
    setClearing(true);
    try { await api.clearAssistantHistory(); } catch { /* reset locally regardless */ }
    sessionRef.current = null;
    setMessages([]);
    setClearing(false);
  }, [busy, clearing]);

  const send = useCallback(async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { id: nextId(), role: "user", content: msg }, { id: nextId(), role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + payload.delta };
              return next;
            });
          }
          if (payload.session_id) sessionRef.current = payload.session_id;
        }
      }
    } catch (e) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { ...next[next.length - 1], content: "Sorry, I couldn't answer that right now. Please try again." };
        return next;
      });
    } finally {
      setBusy(false);
    }
  }, [input, busy]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assistant-dialog" className="flex h-[80vh] max-h-[640px] max-w-lg flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b border-[#D8E0E8] px-5 py-4">
          <DialogTitle className="flex items-center gap-2 pr-9 font-serif text-[#082D4A]">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D4A017]/15 text-[#B0850F]"><Sparkles size={16} /></span>
            Ask about your family
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                disabled={clearing}
                data-testid="assistant-clear-button"
                title="Clear this conversation"
                className="ml-auto flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-[#64748B] transition-colors hover:bg-[#F1F5F9] hover:text-[#082D4A] disabled:opacity-50"
              >
                <Trash2 size={13} /> Clear
              </button>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#64748B]">Powered by Claude · answers from your family tree only</DialogDescription>
        </DialogHeader>

        <div ref={scrollRef} data-testid="assistant-messages" className="flex-1 space-y-4 overflow-y-auto bg-[#F4F6F8] px-5 py-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center gap-4 pt-6 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[#D4A017] shadow-sm"><Sparkles size={26} /></span>
              <p className="max-w-xs text-sm text-[#64748B]">Ask me anything about the people in your tree — relationships, dates, places or stories.</p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)} className="rounded-full border border-[#D8E0E8] bg-white px-3 py-1.5 text-xs font-medium text-[#082D4A] transition-colors hover:bg-[#E8EEF3]">{s}</button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={m.id} data-testid={`assistant-msg-${m.role}-${i}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${m.role === "user" ? "bg-[#082D4A] text-white" : "bg-[#D4A017]/15 text-[#B0850F]"}`}>
                  {m.role === "user" ? <User size={15} /> : <Sparkles size={15} />}
                </span>
                <div className={`max-w-[78%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#082D4A] text-white" : "bg-white text-[#082D4A] shadow-sm"}`}>
                  {m.content || (busy ? <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D4A017] [animation-delay:-0.2s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D4A017] [animation-delay:-0.1s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#D4A017]" /></span> : "")}
                </div>
              </div>
            ))
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-[#D8E0E8] bg-white px-4 py-3">
          <Input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about a person, date or place…"
            className="rounded-full border-[#D8E0E8] focus-visible:ring-[#D4A017]"
            disabled={busy}
          />
          <Button type="submit" data-testid="assistant-send-button" disabled={busy || !input.trim()} className="shrink-0 rounded-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
            <Send size={16} />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
